import { useState, useCallback, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout, Drawer, Grid, Spin } from 'antd';
import AppHeader from './Header';
import AppSidebar from './Sidebar';
import useWebSocket from '../../hooks/useWebSocket';
import usePolling from '../../hooks/usePolling';
import useAlarmStore from '../../stores/alarmStore';
import useDeviceStore from '../../stores/deviceStore';
import useSystemStore from '../../stores/systemStore';
import { REFRESH_INTERVALS } from '../../utils/constants';
import { useEffect } from 'react';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md; // true when < 768px

  const fetchOverview = useDeviceStore((s) => s.fetchOverview);
  const fetchStats = useAlarmStore((s) => s.fetchStats);
  const fetchActiveAlarms = useAlarmStore((s) => s.fetchActiveAlarms);
  const branding = useSystemStore((s) => s.branding);
  const zoomLevel = useSystemStore((s) => s.zoomLevel);
  const zoomIn = useSystemStore((s) => s.zoomIn);
  const zoomOut = useSystemStore((s) => s.zoomOut);

  // 頁面初始載入時套用 localStorage 的 zoom 值（zoom 由 store action 直接操控 DOM）
  useEffect(() => {
    (document.documentElement.style as any).zoom = String(zoomLevel);
  // eslint-disable-next-line
  }, []);

  // Alt+= 放大，Alt+- 縮小 快捷鍵
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      if (e.key === '=' || e.key === '+') { e.preventDefault(); zoomIn(); }
      if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomOut(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomIn, zoomOut]);

  // 點擊非互動元素時自動 blur input，避免 cursor 殘留
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const interactive = target.closest(
      'input, textarea, select, button, a, [role="button"], [role="menuitem"], ' +
      '.ant-input, .ant-select, .ant-switch, .ant-btn, .ant-checkbox, .ant-menu-item, .ant-dropdown'
    );
    if (!interactive && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }, []);

  // WebSocket 連線
  useWebSocket();

  // 輪詢備援
  usePolling(fetchOverview, REFRESH_INTERVALS.DASHBOARD);
  usePolling(fetchStats, REFRESH_INTERVALS.ALARMS);
  usePolling(fetchActiveAlarms, REFRESH_INTERVALS.ALARMS);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop: Fixed Sider */}
      {!isMobile && (
        <Sider
          collapsed={collapsed}
          collapsedWidth={60}
          trigger={null}
          breakpoint="lg"
          theme="light"
          style={{
            position: 'fixed', left: 0, top: 0, bottom: 0,
            borderRight: '1px solid var(--sw-border)',
          }}
        >
          {/* 內部 flex 容器：logo + 選單（可捲動）+ 頭像（固定底部） */}
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Logo */}
            <div style={{
              height: 64, boxSizing: 'border-box',
              // 展開時收窄左右內距，讓 SUNWARD 橫式 logo 能放大（Sider 200 - 2*6 = 188 可用寬）
              padding: collapsed ? '0 8px' : '0 6px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              borderBottom: '1px solid var(--sw-border)',
              transition: 'padding 0.2s', flexShrink: 0,
            }}>
              {collapsed ? (
                // ?v=2：sunward-stacked.png 之 SUNWARD 文字已改黑，public 圖無 content hash，
                // 附版本參數強制瀏覽器重抓（避免 HTTP 快取續用舊紅字版）。
                <img src={`${branding.logo_icon}${branding.logo_icon.includes('?') ? '&' : '?'}v=2`} alt={branding.subtitle}
                  style={{ width: 40, height: 40, objectFit: 'contain' }} />
              ) : (
                <img src={branding.logo} alt={branding.name}
                  style={{ width: '100%', maxWidth: 188, height: 'auto' }} />
              )}
            </div>

            {/* 選單 + 底部使用者頭像 */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <AppSidebar collapsed={collapsed} onCollapse={() => setCollapsed(!collapsed)} />
            </div>
          </div>
        </Sider>
      )}

      {/* Mobile: Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          width={240}
          styles={{
            body: { padding: 0 },
            header: { padding: '0 16px', height: 64, borderBottom: '1px solid var(--sw-border)', display: 'flex', alignItems: 'center' },
          }}
          title={
            <img src={branding.logo} alt={branding.name}
              style={{ height: 40, objectFit: 'contain', maxWidth: 180 }} />
          }
          closeIcon={null}
        >
          <AppSidebar onClose={() => setMobileMenuOpen(false)} />
        </Drawer>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 60 : 200), transition: 'margin-left 0.2s' }}>
        <Header style={{
          padding: 0, position: 'sticky', top: 0, zIndex: 10,
          borderBottom: '1px solid var(--sw-border)',
          height: isMobile ? 'auto' : 64, boxSizing: 'border-box', lineHeight: 'normal',
        }}>
          <AppHeader isMobile={isMobile} onMobileMenuOpen={() => setMobileMenuOpen(true)} />
        </Header>
        <Content
          style={{ margin: isMobile ? 8 : 16, overflow: 'auto', background: 'transparent' }}
          onClick={handleContentClick}
        >
          <Suspense fallback={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <Spin size="large" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}
